import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";

const t = copy.websites;

const section = "border-t border-ground-edge px-4 py-20 md:px-6 md:py-28";
const eyebrow = "text-[11px] uppercase tracking-[0.24em] text-chrome-dim";
const h2 = "mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.01em] text-chrome md:text-6xl";

function Tile({ icon, size = "h-12 w-12 text-2xl" }: { icon: string; size?: string }) {
  return (
    <span className={`flex shrink-0 items-center justify-center rounded-md bg-gold/10 text-gold ${size}`}>
      <Icon name={icon as IconName} />
    </span>
  );
}

// The editor is a Go page: a plain anchor.
export const EDITOR_HREF = "/editor";

export function Hero() {
  return (
    <section id="top" className="relative overflow-hidden border-b border-ground-edge pt-32 md:pt-40">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(247,201,72,0.12)_0%,transparent_60%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "linear-gradient(rgba(36,36,36,0.9) 1px, transparent 1px), linear-gradient(90deg, rgba(36,36,36,0.9) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      <div className="relative z-10 mx-auto w-full max-w-[1240px] px-4 pb-20 md:px-6 md:pb-28">
        <p className={eyebrow}>{t.hero.eyebrow}</p>
        <h1 className="mt-5 max-w-4xl font-headline text-5xl uppercase leading-[0.92] tracking-[-0.02em] text-chrome sm:text-6xl md:text-7xl lg:text-8xl">
          {t.hero.heading[0]} <span className="mt-2 block text-gold">{t.hero.heading[1]}</span>
        </h1>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-chrome-dim md:text-lg">{t.hero.sub}</p>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a
            href={EDITOR_HREF}
            className="bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-money px-6 text-xs font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep"
          >
            {t.hero.build}
          </a>
          <a
            href="#pricing"
            className="bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md border border-gold/60 px-6 text-xs font-bold uppercase tracking-[0.14em] text-gold transition-colors hover:bg-gold/10"
          >
            {t.hero.pricing}
          </a>
        </div>
        <div className="mt-20 md:mt-28">
          <div className="flex items-center gap-4">
            <span className="h-px flex-1 bg-ground-edge" />
            <span className={eyebrow}>{t.hero.unlocks}</span>
            <span className="h-px flex-1 bg-ground-edge" />
          </div>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {t.hero.values.map((v) => (
              <li key={v.title} className="rounded-2xl border border-ground-edge bg-ground-raised p-5 transition-colors hover:border-gold/40">
                <Tile icon={v.icon} size="h-11 w-11 text-2xl" />
                <h3 className="mt-5 font-headline text-lg uppercase tracking-[0.01em] text-chrome">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-chrome-dim">{v.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

export function AudienceFilter() {
  return (
    <section className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.audience.eyebrow}</p>
          <h2 className={h2}>{t.audience.heading}</h2>
        </div>
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          {t.audience.items.map((item) => (
            <li
              key={item.text}
              className="flex items-start gap-4 rounded-2xl border border-ground-edge bg-ground-raised p-5 transition-colors hover:border-gold/40 md:p-6"
            >
              <Tile icon={item.icon} />
              <p className="pt-2 text-base leading-relaxed text-chrome md:text-lg">{item.text}</p>
            </li>
          ))}
        </ul>
        <p className="mt-10 text-sm italic text-chrome-dim">{t.audience.note}</p>
      </div>
    </section>
  );
}

export function BeforeAfter() {
  return (
    <section className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.shift.eyebrow}</p>
          <h2 className={h2}>{t.shift.heading}</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          <div className="rounded-2xl border border-ground-edge bg-ground p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-ground-raised text-2xl text-chrome-dim">
                <Icon name="close" />
              </span>
              <h3 className="font-headline text-2xl uppercase tracking-[0.01em] text-chrome-dim">{t.shift.before}</h3>
            </div>
            <ul className="mt-6 space-y-3">
              {t.shift.beforeItems.map((item) => (
                <li key={item.text} className="flex items-center gap-3 rounded-lg border border-ground-edge bg-ground-raised p-4">
                  <Icon name={item.icon as IconName} className="text-xl text-chrome-dim" />
                  <span className="text-sm text-chrome-dim">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-money/30 bg-money/5 p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-money/15 text-2xl text-money">
                <Icon name="check" />
              </span>
              <h3 className="font-headline text-2xl uppercase tracking-[0.01em] text-chrome">{t.shift.after}</h3>
            </div>
            <ul className="mt-6 space-y-3">
              {t.shift.afterItems.map((item) => (
                <li key={item.text} className="flex items-center gap-3 rounded-lg border border-money/20 bg-ground p-4">
                  <Icon name={item.icon as IconName} className="text-xl text-money" />
                  <span className="text-sm text-chrome">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export function InteractionHub() {
  return (
    <section className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-6xl">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.tools.eyebrow}</p>
          <h2 className={h2}>
            {t.tools.heading[0]} <span className="text-gold">{t.tools.heading[1]}</span>
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.tools.sub}</p>
        </div>
        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
          {t.tools.items.map((tool) => (
            <li
              key={tool.title}
              className="group rounded-2xl border border-ground-edge bg-ground-raised p-6 transition-colors hover:border-gold/50 md:p-8"
            >
              <Tile icon={tool.icon} size="h-14 w-14 rounded-xl text-3xl" />
              <h3 className="mt-6 font-headline text-2xl uppercase tracking-[0.01em] text-chrome transition-colors group-hover:text-gold">
                {tool.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-chrome-dim md:text-base">{tool.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function CoSign() {
  return (
    <section className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.cosign.eyebrow}</p>
          <h2 className={h2}>{t.cosign.heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.cosign.sub}</p>
        </div>
        <ol className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
          {t.cosign.steps.map((step, i) => (
            <li key={step.title} className="relative">
              <div className="h-full rounded-2xl border border-ground-edge bg-ground-raised p-6 transition-colors hover:border-gold/40 md:p-8">
                <p className="bf-tabular font-headline text-5xl leading-none text-gold/25">{String(i + 1).padStart(2, "0")}</p>
                <span className="mt-4 block">
                  <Tile icon={step.icon} />
                </span>
                <h3 className="mt-4 font-headline text-xl uppercase tracking-[0.01em] text-chrome">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-chrome-dim">{step.body}</p>
              </div>
              {i < t.cosign.steps.length - 1 && (
                <span className="absolute -right-5 top-1/2 hidden -translate-y-1/2 text-2xl text-gold/40 md:block">
                  <Icon name="arrowRight" />
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function BeatStoreIntegration() {
  return (
    <section className={`${section} bg-ground-raised`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.store.eyebrow}</p>
          <h2 className={h2}>{t.store.heading}</h2>
          <p className="mt-5 text-sm leading-relaxed text-chrome-dim md:text-base">{t.store.sub}</p>
        </div>
        <div className="mt-12 grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <ul className="grid grid-cols-2 gap-4">
              {t.store.platforms.map((p) => (
                <li key={p.name} className="group rounded-xl border border-ground-edge bg-ground p-6 transition-colors hover:border-gold/50">
                  <Tile icon={p.icon} />
                  <div className="mt-4 text-sm font-bold text-chrome">{p.name}</div>
                  <div className="mt-1 text-xs text-chrome-dim">{t.store.embed}</div>
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-xl border border-gold/25 bg-gold/5 p-6">
              <div className="flex items-start gap-3">
                <Icon name="info" className="mt-0.5 text-xl text-gold" />
                <div>
                  <div className="text-sm font-bold text-chrome">{t.store.keepTitle}</div>
                  <div className="mt-1 text-sm leading-relaxed text-chrome-dim">{t.store.keepBody}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-ground-edge bg-ground p-6 md:p-8">
            <span className="inline-block rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-gold">
              {t.store.siteLabel}
            </span>
            <ul className="mt-6 space-y-3">
              {t.store.modules.map((m) => {
                const store = "store" in m && m.store;
                return (
                  <li
                    key={m.label}
                    className={`flex items-center gap-3 rounded-lg border p-4 ${
                      store ? "border-money/30 bg-money/10" : "border-ground-edge bg-ground-raised"
                    }`}
                  >
                    <Icon name={m.icon as IconName} className={`text-lg ${store ? "text-money" : "text-gold"}`} />
                    <span className={`text-sm ${store ? "font-bold text-chrome" : "text-chrome-dim"}`}>{m.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhatYouGet() {
  return (
    <section id="features" className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="max-w-3xl">
          <p className={eyebrow}>{t.included.eyebrow}</p>
          <h2 className={h2}>{t.included.heading}</h2>
        </div>
        <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
          {t.included.items.map((f) => (
            <li key={f.title} className="group rounded-xl border border-ground-edge bg-ground-raised p-6 transition-colors hover:border-gold/50">
              <Tile icon={f.icon} />
              <h3 className="mt-4 text-sm font-bold text-chrome">{f.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-chrome-dim">{f.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section className={`${section} bg-ground`}>
      <div className="mx-auto w-full max-w-4xl">
        <h2 className="font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-6xl">
          {t.final.heading[0]} <br />
          <span className="text-gold">{t.final.heading[1]}</span>
        </h2>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-chrome-dim md:text-base">{t.final.sub}</p>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
          <a
            href={EDITOR_HREF}
            className="bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-money px-6 text-xs font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep"
          >
            {t.hero.build}
          </a>
          <a
            href="#pricing"
            className="bf-focusable flex h-12 items-center justify-center whitespace-nowrap rounded-md border border-gold/60 px-6 text-xs font-bold uppercase tracking-[0.14em] text-gold transition-colors hover:bg-gold/10"
          >
            {t.hero.pricing}
          </a>
        </div>
        <ul className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
          {t.final.trust.map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm text-chrome-dim">
              <Icon name="check" className="text-money" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
